// MCHD Room Scheduler - Audit Logs & Reports Page
// Main page for audit logs viewing and compliance reporting

import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Card,
  Stack,
  Badge,
  Button,
  Box,
  Alert,
  Grid,
  Tabs,
  TextInput,
  Select,
  NumberInput,
  Table,
  ScrollArea,
  ActionIcon,
  Tooltip,
  Modal,
  Progress,
  Divider,
} from '@mantine/core';
import { ModernDatePicker } from '../../../components/molecules/ModernDatePicker';
import {
  IconFileText,
  IconDownload,
  IconFilter,
  IconEye,
  IconCalendar,
  IconUsers,
  IconActivity,
  IconChartBar,
  IconAlertCircle,
  IconUser,
  IconDeviceDesktop,
} from '@tabler/icons-react';
import { useAuditLogs } from '../../../hooks/admin/useAuditLogs';
import DynamicRefreshButton from '../../../components/atoms/DynamicRefreshButton';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { AuditLogFilters, ExportOptions } from '../../../api/endpoints/auth';

/**
 * Audit logs and reports page for administrators
 * @returns JSX element
 */
export function AuditLogsReports(): JSX.Element {
  const [activeTab, setActiveTab] = useState<string | null>('logs');
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const {
    logs,
    stats,
    report,
    isLoading,
    isGeneratingReport,
    isExporting,
    error,
    fetchLogs,
    generateReport,
    exportLogs,
    refresh,
  } = useAuditLogs();

  // Filter form
  const filterForm = useForm({
    initialValues: {
      userId: '',
      eventType: '',
      dateRange: [null, null] as [Date | null, Date | null],
      limit: 100,
    },
  });

  // Debug: Log stats when they change
  useEffect(() => {
    console.log('Audit Logs Stats Updated:', stats);
    console.log('Event Types from API:', stats.eventTypes);
    console.log('Available Event Types:', getAvailableEventTypes());
  }, [stats]);

  // Report generation form
  const reportForm = useForm({
    initialValues: {
      dateRange: [null, null] as [Date | null, Date | null],
    },
    validate: {
      dateRange: (value: [Date | null, Date | null]) => {
        if (!value[0]) return 'Start date is required';
        if (!value[1]) return 'End date is required';
        if (value[0] && value[1] && value[1] < value[0]) {
          return 'End date must be after start date';
        }
        return null;
      },
    },
  });

  // Export form
  const exportForm = useForm({
    initialValues: {
      dateRange: [null, null] as [Date | null, Date | null],
      format: 'json' as 'json' | 'csv',
    },
  });

  /**
   * Handle filter application
   */
  const handleApplyFilters = () => {
    const filters: AuditLogFilters = {
      limit: filterForm.values.limit,
    };

    if (filterForm.values.userId) {
      filters.userId = filterForm.values.userId;
    }
    if (filterForm.values.eventType) {
      filters.eventType = filterForm.values.eventType;
    }
    if (filterForm.values.dateRange[0]) {
      filters.startDate = filterForm.values.dateRange[0].toISOString();
    }
    if (filterForm.values.dateRange[1]) {
      filters.endDate = filterForm.values.dateRange[1].toISOString();
    }

    fetchLogs(filters);
    setFiltersModalOpen(false);
  };

  /**
   * Handle report generation
   */
  const handleGenerateReport = async () => {
    if (!reportForm.validate().hasErrors) {
      await generateReport(
        reportForm.values.dateRange[0]!.toISOString(),
        reportForm.values.dateRange[1]!.toISOString()
      );
      setActiveTab('report');
    }
  };

  /**
   * Handle export
   */
  const handleExport = async () => {
    const exportOptions: ExportOptions = {
      format: exportForm.values.format,
    };

    if (exportForm.values.dateRange[0]) {
      exportOptions.startDate = exportForm.values.dateRange[0].toISOString();
    }
    if (exportForm.values.dateRange[1]) {
      exportOptions.endDate = exportForm.values.dateRange[1].toISOString();
    }

    const result = await exportLogs(exportOptions);
    if (result) {
      // Generate proper filename
      const filename = generateExportFilename(
        exportForm.values.dateRange[0],
        exportForm.values.dateRange[1],
        0, // recordCount not available in API response, will be estimated
        exportForm.values.format
      );

      // Create download link
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notifications.show({
        title: 'Export Successful',
        message: `Audit logs exported as ${filename}`,
        color: 'green',
      });
    }
    setExportModalOpen(false);
  };

  /**
   * Generate proper export filename
   */
  /**
   * Generate date range presets
   */
  const getDateRangePresets = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return [
      {
        label: 'Last 7 days',
        value: [new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), today] as [Date, Date],
      },
      {
        label: 'Last 30 days',
        value: [new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), today] as [Date, Date],
      },
      {
        label: 'Last 3 months',
        value: [new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), today] as [Date, Date],
      },
      {
        label: 'This month',
        value: [new Date(today.getFullYear(), today.getMonth(), 1), today] as [Date, Date],
      },
      {
        label: 'Last month',
        value: [
          new Date(today.getFullYear(), today.getMonth() - 1, 1),
          new Date(today.getFullYear(), today.getMonth(), 0),
        ] as [Date, Date],
      },
    ];
  };

  const generateExportFilename = (
    startDate: Date | null,
    endDate: Date | null,
    recordCount: number,
    format: string
  ): string => {
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };

    let dateRange: string;
    if (startDate && endDate) {
      dateRange = `${formatDate(startDate)}-to-${formatDate(endDate)}`;
    } else if (startDate) {
      dateRange = formatDate(startDate);
    } else {
      dateRange = formatDate(new Date());
    }

    return `audit-logs-${dateRange}-${recordCount}.${format}`;
  };

  /**
   * Predefined event types for fallback
   */
  const PREDEFINED_EVENT_TYPES = [
    'LOGIN',
    'LOGOUT',
    'PASSWORD_CHANGE',
    'USER_CREATE',
    'USER_UPDATE',
    'USER_DELETE',
    'ROOM_ASSIGN',
    'ROOM_UNASSIGN',
    'ACCOUNT_LOCKED',
    'ACCOUNT_UNLOCKED',
    'SESSION_CREATE',
    'SESSION_DELETE',
    'ERROR',
    'SECURITY',
    'AUDIT_LOG_VIEW',
    'AUDIT_LOG_EXPORT',
    'REPORT_GENERATE',
  ];

  /**
   * Get available event types (from API or fallback)
   */
  const getAvailableEventTypes = () => {
    const apiEventTypes = Array.isArray(stats.eventTypes) ? stats.eventTypes : [];
    console.log('API Event Types:', apiEventTypes); // Debug
    console.log('Stats object:', stats); // Debug
    
    // If API returns event types, use them; otherwise use predefined list
    const eventTypes = apiEventTypes.length > 0 ? apiEventTypes : PREDEFINED_EVENT_TYPES;
    console.log('Final Event Types:', eventTypes); // Debug
    
    return eventTypes.map(type => ({ value: type, label: type }));
  };

  /**
   * Get event type color
   */
  const getEventTypeColor = (eventType: string) => {
    const colors: { [key: string]: string } = {
      'LOGIN': 'green',
      'LOGOUT': 'blue',
      'PASSWORD_CHANGE': 'yellow',
      'USER_CREATE': 'purple',
      'USER_UPDATE': 'orange',
      'USER_DELETE': 'red',
      'ROOM_ASSIGN': 'cyan',
      'ROOM_UNASSIGN': 'grape',
      'ACCOUNT_LOCKED': 'red',
      'ACCOUNT_UNLOCKED': 'green',
      'SESSION_CREATE': 'blue',
      'SESSION_DELETE': 'orange',
      'ERROR': 'red',
      'SECURITY': 'pink',
      'AUDIT_LOG_VIEW': 'grape',
      'AUDIT_LOG_EXPORT': 'cyan',
      'REPORT_GENERATE': 'yellow',
    };
    return colors[eventType] || 'gray';
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Audit Logs"
          color="red"
          variant="light"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={1} mb="xs">
              Audit Logs & Reports
            </Title>
            <Text size="lg" c="dimmed">
              Monitor system activity and generate compliance reports
            </Text>
          </Box>
          <Group gap="sm">
            <DynamicRefreshButton
              variant="outline"
              onRefresh={() => refresh()}
              isLoading={isLoading}
              size="sm"
              color="blue"
            />
            <Button
              variant="outline"
              leftSection={<IconFilter size={16} />}
              onClick={() => setFiltersModalOpen(true)}
              size="sm"
              color="blue"
              styles={{
                root: {
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.25)',
                  },
                  '&:active': {
                    transform: 'translateY(0) scale(0.98)',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                  },
                },
              }}
            >
              Filters
            </Button>
            <Button
              variant="outline"
              leftSection={<IconDownload size={16} />}
              onClick={() => setExportModalOpen(true)}
              loading={isExporting}
              size="sm"
              color="blue"
              styles={{
                root: {
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.25)',
                  },
                  '&:active': {
                    transform: 'translateY(0) scale(0.98)',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                  },
                },
              }}
            >
              Export
            </Button>
          </Group>
        </Group>

        {/* Statistics Overview */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Total Logs
                </Text>
                <IconFileText size={20} color="var(--mantine-color-blue-6)" />
              </Group>
              <Text size="xl" fw={700} c="blue">
                {isLoading ? '...' : (stats.totalLogs || 0).toLocaleString()}
              </Text>
              <Text size="xs" c="dimmed">
                All audit entries
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Unique Users
                </Text>
                <IconUsers size={20} color="var(--mantine-color-green-6)" />
              </Group>
              <Text size="xl" fw={700} c="green">
                {isLoading ? '...' : (stats.uniqueUsers || 0)}
              </Text>
              <Text size="xs" c="dimmed">
                Active users
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Event Types
                </Text>
                <IconActivity size={20} color="var(--mantine-color-purple-6)" />
              </Group>
              <Text size="xl" fw={700} c="purple">
                {isLoading ? '...' : (Array.isArray(stats.eventTypes) ? stats.eventTypes.length : 0)}
              </Text>
              <Text size="xs" c="dimmed">
                Different actions
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Date Range
                </Text>
                <IconCalendar size={20} color="var(--mantine-color-orange-6)" />
              </Group>
              <Text size="sm" fw={700} c="orange">
                {isLoading ? '...' : (stats.dateRange?.start && stats.dateRange?.end 
                  ? `${new Date(stats.dateRange.start).toLocaleDateString()} - ${new Date(stats.dateRange.end).toLocaleDateString()}`
                  : 'All time'
                )}
              </Text>
              <Text size="xs" c="dimmed">
                Coverage period
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="logs" leftSection={<IconFileText size={16} />}>
              Audit Logs
            </Tabs.Tab>
            <Tabs.Tab value="report" leftSection={<IconChartBar size={16} />}>
              Compliance Report
            </Tabs.Tab>
          </Tabs.List>

          {/* Audit Logs Tab */}
          <Tabs.Panel value="logs" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text size="lg" fw={600}>
                  Audit Logs
                </Text>
                <Group gap="sm">
                  <Text size="sm" c="dimmed">
                    {logs.length} entries
                  </Text>
                </Group>
              </Group>

              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Timestamp</Table.Th>
                      <Table.Th>User</Table.Th>
                      <Table.Th>Action</Table.Th>
                      <Table.Th>Entity</Table.Th>
                      <Table.Th>IP Address</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {logs.map((log) => (
                      <Table.Tr key={log.id}>
                        <Table.Td>
                          <Text size="sm">{formatDate(log.createdAt)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <IconUser size={14} />
                            <Text size="sm">{log.userId || 'System'}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={getEventTypeColor(log.action)}
                            variant="light"
                            size="sm"
                          >
                            {log.action}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{log.entityType || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <IconDeviceDesktop size={14} />
                            <Text size="sm">{log.ipAddress || 'N/A'}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label="View Details">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => setSelectedLog(log)}
                            >
                              <IconEye size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Card>
          </Tabs.Panel>

          {/* Compliance Report Tab */}
          <Tabs.Panel value="report" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              {/* Report Generation Controls */}
              <Stack gap="md" mb="lg">
                <Text size="lg" fw={600}>
                  Compliance Report
                </Text>

                {/* Modern Floating Date Range Selection for Report */}
                <Box>
                  <Text size="sm" fw={500} mb="xs">Select Date Range for Report</Text>
                  <Group gap="xs" mb="sm">
                    {getDateRangePresets().map((preset) => (
                      <Button
                        key={preset.label}
                        variant="outline"
                        size="xs"
                        onClick={() => reportForm.setFieldValue('dateRange', preset.value)}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Group>
                  <Group grow>
                    <ModernDatePicker
                      key={`report-start-${reportForm.values.dateRange[0]?.getTime() || 'empty'}`}
                      placeholder="Start date"
                      value={reportForm.values.dateRange[0]}
                      onChange={(date) => reportForm.setFieldValue('dateRange', [date, reportForm.values.dateRange[1]])}
                      popoverProps={{ withinPortal: true }}
                      {...(reportForm.values.dateRange[1] && { maxDate: reportForm.values.dateRange[1] })}
                      size="md"
                    />
                    <ModernDatePicker
                      key={`report-end-${reportForm.values.dateRange[1]?.getTime() || 'empty'}`}
                      placeholder="End date"
                      value={reportForm.values.dateRange[1]}
                      onChange={(date) => reportForm.setFieldValue('dateRange', [reportForm.values.dateRange[0], date])}
                      popoverProps={{ withinPortal: true }}
                      {...(reportForm.values.dateRange[0] && { minDate: reportForm.values.dateRange[0] })}
                      size="md"
                    />
                  </Group>
                  {reportForm.values.dateRange[0] && reportForm.values.dateRange[1] && (
                    <Button
                      variant="subtle"
                      size="xs"
                      mt="xs"
                      onClick={() => reportForm.setFieldValue('dateRange', [null, null])}
                    >
                      Clear dates
                    </Button>
                  )}
                </Box>

                <Group justify="flex-end">
                  <Button
                    leftSection={<IconChartBar size={16} />}
                    onClick={handleGenerateReport}
                    loading={isGeneratingReport}
                    disabled={!reportForm.values.dateRange[0] || !reportForm.values.dateRange[1]}
                  >
                    Generate Report
                  </Button>
                </Group>
              </Stack>

              {report ? (
                <Stack gap="md">
                  {/* Report Summary */}
                  <Card withBorder>
                    <Text size="md" fw={600} mb="md">Report Summary</Text>
                    <Grid>
                      <Grid.Col span={6}>
                        <Text size="sm" fw={500}>Total Actions</Text>
                        <Text size="xl" fw={700} c="blue">{report.summary.totalActions}</Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" fw={500}>Unique Users</Text>
                        <Text size="xl" fw={700} c="green">{report.summary.uniqueUsers}</Text>
                      </Grid.Col>
                    </Grid>
                  </Card>

                  {/* Action Breakdown */}
                  <Card withBorder>
                    <Text size="md" fw={600} mb="md">Action Breakdown</Text>
                    <Stack gap="sm">
                      {Object.entries(report.actions).map(([action, count]) => (
                        <Box key={action}>
                          <Group justify="space-between" mb="xs">
                            <Text size="sm">{action}</Text>
                            <Text size="sm" fw={500}>{count}</Text>
                          </Group>
                          <Progress
                            value={(count / report.summary.totalActions) * 100}
                            color={getEventTypeColor(action)}
                            size="sm"
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                </Stack>
              ) : (
                <Alert
                  icon={<IconChartBar size={16} />}
                  title="No Report Generated"
                  color="blue"
                  variant="light"
                >
                  Generate a compliance report by selecting a date range and clicking "Generate Report".
                </Alert>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Filters Modal */}
        <Modal
          opened={filtersModalOpen}
          onClose={() => setFiltersModalOpen(false)}
          title="Filter Audit Logs"
          size="md"
        >
          <form onSubmit={filterForm.onSubmit(handleApplyFilters)}>
            <Stack gap="md">
              <TextInput
                label="User ID"
                placeholder="Enter user ID"
                {...filterForm.getInputProps('userId')}
              />
              <Select
                label="Event Type"
                placeholder="Select event type"
                data={getAvailableEventTypes()}
                {...filterForm.getInputProps('eventType')}
                searchable={true}
                clearable={true}
                styles={{
                  input: {
                    cursor: 'pointer',
                  },
                  option: {
                    cursor: 'pointer',
                  },
                }}
              />
              {/* Modern Floating Date Range Selection */}
              <Box>
                <Text size="sm" fw={500} mb="xs">Date Range</Text>
                <Group gap="xs" mb="sm">
                  {getDateRangePresets().map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      size="xs"
                      onClick={() => filterForm.setFieldValue('dateRange', preset.value)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Group>
                <Group grow>
                  <ModernDatePicker
                    key={`start-${filterForm.values.dateRange[0]?.getTime() || 'empty'}`}
                    placeholder="Start date"
                    value={filterForm.values.dateRange[0]}
                    onChange={(date) => filterForm.setFieldValue('dateRange', [date, filterForm.values.dateRange[1]])}
                    popoverProps={{ withinPortal: true }}
                    {...(filterForm.values.dateRange[1] && { maxDate: filterForm.values.dateRange[1] })}
                    size="md"
                  />
                  <ModernDatePicker
                    key={`end-${filterForm.values.dateRange[1]?.getTime() || 'empty'}`}
                    placeholder="End date"
                    value={filterForm.values.dateRange[1]}
                    onChange={(date) => filterForm.setFieldValue('dateRange', [filterForm.values.dateRange[0], date])}
                    popoverProps={{ withinPortal: true }}
                    {...(filterForm.values.dateRange[0] && { minDate: filterForm.values.dateRange[0] })}
                    size="md"
                  />
                </Group>
                {filterForm.values.dateRange[0] && filterForm.values.dateRange[1] && (
                  <Button
                    variant="subtle"
                    size="xs"
                    mt="xs"
                    onClick={() => filterForm.setFieldValue('dateRange', [null, null])}
                  >
                    Clear dates
                  </Button>
                )}
              </Box>
              <NumberInput
                label="Limit"
                placeholder="Number of entries"
                min={1}
                max={1000}
                {...filterForm.getInputProps('limit')}
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="outline" onClick={() => setFiltersModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Apply Filters</Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Export Modal */}
        <Modal
          opened={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          title="Export Audit Logs"
          size="md"
        >
          <form onSubmit={exportForm.onSubmit(handleExport)}>
            <Stack gap="md">
              {/* Modern Floating Date Range Selection for Export */}
              <Box>
                <Text size="sm" fw={500} mb="xs">Date Range (optional)</Text>
                <Group gap="xs" mb="sm">
                  {getDateRangePresets().map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      size="xs"
                      onClick={() => exportForm.setFieldValue('dateRange', preset.value)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Group>
                <Group grow>
                  <ModernDatePicker
                    key={`export-start-${exportForm.values.dateRange[0]?.getTime() || 'empty'}`}
                    placeholder="Start date"
                    value={exportForm.values.dateRange[0]}
                    onChange={(date) => exportForm.setFieldValue('dateRange', [date, exportForm.values.dateRange[1]])}
                    popoverProps={{ withinPortal: true }}
                    {...(exportForm.values.dateRange[1] && { maxDate: exportForm.values.dateRange[1] })}
                    size="md"
                  />
                  <ModernDatePicker
                    key={`export-end-${exportForm.values.dateRange[1]?.getTime() || 'empty'}`}
                    placeholder="End date"
                    value={exportForm.values.dateRange[1]}
                    onChange={(date) => exportForm.setFieldValue('dateRange', [exportForm.values.dateRange[0], date])}
                    popoverProps={{ withinPortal: true }}
                    {...(exportForm.values.dateRange[0] && { minDate: exportForm.values.dateRange[0] })}
                    size="md"
                  />
                </Group>
                {exportForm.values.dateRange[0] && exportForm.values.dateRange[1] && (
                  <Button
                    variant="subtle"
                    size="xs"
                    mt="xs"
                    onClick={() => exportForm.setFieldValue('dateRange', [null, null])}
                  >
                    Clear dates
                  </Button>
                )}
              </Box>
              <Select
                label="Export Format"
                data={[
                  { value: 'json', label: 'JSON' },
                  { value: 'csv', label: 'CSV' },
                ]}
                {...exportForm.getInputProps('format')}
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="outline" onClick={() => setExportModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={isExporting}>Export</Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Log Details Modal */}
        <Modal
          opened={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Log Details"
          size="lg"
        >
          {selectedLog && (
            <Stack gap="md">
              <Grid>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Timestamp</Text>
                  <Text size="sm">{formatDate(selectedLog.createdAt)}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>User ID</Text>
                  <Text size="sm">{selectedLog.userId || 'System'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Action</Text>
                  <Badge color={getEventTypeColor(selectedLog.action)} variant="light">
                    {selectedLog.action}
                  </Badge>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Entity Type</Text>
                  <Text size="sm">{selectedLog.entityType || 'N/A'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>Entity ID</Text>
                  <Text size="sm">{selectedLog.entityId || 'N/A'}</Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="sm" fw={500}>IP Address</Text>
                  <Text size="sm">{selectedLog.ipAddress || 'N/A'}</Text>
                </Grid.Col>
              </Grid>
              
              {selectedLog.details && (
                <>
                  <Divider />
                  <Box>
                    <Text size="sm" fw={500} mb="xs">Details</Text>
                    <Text size="sm" c="dimmed">
                      {typeof selectedLog.details === 'string' 
                        ? selectedLog.details 
                        : JSON.stringify(selectedLog.details, null, 2)
                      }
                    </Text>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </Modal>
      </Stack>
    </Container>
  );
}
